import { deepMerge } from '@soitron/global-helpers';
/**
 * @class CameraSwitcher
 * @description Provides functionality to retrieve the current camera facing mode and
 * switch (toggle) between available video input devices (cameras) managed by the CameraHandler.
 */
export class CameraSwitcher {
    _cameraStreamHandler;
    /**
     * @constructor
     * @description Initializes a new instance of the CameraSwitcher.
     * @param {CameraHandler} _cameraStreamHandler - A reference to the CameraHandler instance, which manages the stream lifecycle and configuration.
     */
    constructor(_cameraStreamHandler) {
        this._cameraStreamHandler = _cameraStreamHandler;
    }
    /**
     * @public
     * @type {ConstrainDOMString}
     * @description Gets the facing mode ('user', 'environment', or 'unknown') of the currently active camera track.
     * It also ensures the horizontal flip is applied correctly based on the facing mode.
     * Uses the required double underscore prefix for public accessors.
     * @returns {ConstrainDOMString} The camera facing mode. Returns 'unknown' if the track is inactive or the mode is not 'user'/'environment'.
     * @example
     * const mode = switcher.__getCurrentCameraFacingMode;
     * // mode will be 'user' or 'environment'
     */
    get __getCurrentCameraFacingMode() {
        // Retrieve the current video track from the CameraHandler.
        const currentTrack = this._cameraStreamHandler.__getCurrentTrack;
        // Check if a track is active.
        if (!currentTrack) {
            // If no track is active, return 'unknown' mode.
            return 'unknown';
        }
        // Get the active track's applied settings.
        const settings = currentTrack.getSettings();
        // Extract the facing mode property from the settings.
        const facingMode = settings.facingMode;
        // Apply the horizontal flip transformation to the video element based on the detected facing mode.
        this._cameraStreamHandler.__applyHorizontalFlip(facingMode);
        // Check if the facing mode is one of the standard accepted values.
        if (facingMode === 'user' || facingMode === 'environment') {
            // Return the recognized facing mode.
            return facingMode;
        }
        // If the facing mode is neither 'user' nor 'environment' (e.g., 'left', 'right', or undefined), return 'unknown'.
        return 'unknown';
    }
    // --- PUBLIC METHODS ---
    /**
     * @public
     * @async
     * @method
     * @description Cycles to the next available camera device and restarts the stream with the new device ID constraint.
     * Uses the required double underscore prefix for public methods.
     * @returns {Promise<boolean>} A Promise that resolves to true if the camera was successfully toggled and restarted, false otherwise.
     * @example
     * await switcher.__toggleCameraDevice();
     */
    async __toggleCameraDevice() {
        // Check if camera switching is supported AND if a current device ID is active.
        if (!this._cameraStreamHandler.__canToggleCamera || !this._cameraStreamHandler.__getCurrentDeviceId) {
            // If conditions are not met (e.g., only one camera, or stream is not yet active), return false.
            return false;
        }
        // Get the list of all available video device objects.
        const availableDevices = this._cameraStreamHandler.__getDevicesList;
        // Map the device objects to an array containing only their device IDs.
        const deviceIds = availableDevices.map((d) => d.deviceId);
        // Find the index of the currently active device ID in the list of all device IDs.
        const currentIndex = deviceIds.indexOf(this._cameraStreamHandler.__getCurrentDeviceId);
        // Calculate the index of the next camera in a circular manner (loops back to 0).
        const nextIndex = (currentIndex + 1) % deviceIds.length;
        // Retrieve the device ID for the next camera.
        const nextDeviceId = deviceIds[nextIndex];
        // Ensure a valid next device ID was found (should be true if __canToggleCamera is true).
        if (!nextDeviceId) {
            // If for some reason the ID is missing, return false.
            return false;
        }
        // Update the CameraHandler's configuration to enforce the selection of the next device ID.
        this._cameraStreamHandler.__cameraVideoConfig = deepMerge(this._cameraStreamHandler.__cameraVideoConfig, {
            // Set the deviceId constraint using 'exact' to ensure the specific device is requested.
            deviceId: { exact: nextDeviceId },
        });
        // Stop the current stream, start a new one with the updated configuration, and begin playback.
        await this._cameraStreamHandler.__restartStreamAndPlay();
        // Return true to indicate the toggle operation was successfully initiated.
        return true;
    }
}
//# sourceMappingURL=camera-switcher.js.map